import { FC, KeyboardEvent, useMemo, useState } from 'react';
import { FieldError, RegisterOptions, useForm, ValidationRule } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box, Button, Input, InputProps } from '@robocorp/components';
import { IconCloseSmall } from '@robocorp/icons';
import { styled } from '@robocorp/theme';
import { IconCheckmark, IconEdit } from '@robocorp/icons/iconic';
import UpgradeInfo, { UpgradeInfoProps } from './UpgradeInfo';
import { ListContent, ListItem } from './old-ds-components';

const StyledListItem = styled(ListItem)`
  align-items: start;
`;

/**
 * https://github.com/colinhacks/zod/blob/cc8ad1981ba580d1250520fde8878073d4b7d40a/src/types.ts#L643
 * After next zod release this can be replaced with `z.number().multipleOf(step)`
 */
const floatSafeRemainder = (val: number, step: number) => {
  const valDecCount = (val.toString().split('.')[1] || '').length;
  const stepDecCount = (step.toString().split('.')[1] || '').length;
  const decCount = valDecCount > stepDecCount ? valDecCount : stepDecCount;
  const valInt = parseInt(val.toFixed(decCount).replace('.', ''), 10);
  const stepInt = parseInt(step.toFixed(decCount).replace('.', ''), 10);
  return (valInt % stepInt) / 10 ** decCount;
};

type Form = { inputField: number | string };

type OptionInputProps = Pick<
  RegisterOptions<Form, 'inputField'>,
  'min' | 'max' | 'maxLength' | 'minLength' | 'required'
> &
  Partial<{
    pattern: ValidationRule<string>;
    step: ValidationRule<number>;
  }>;

interface UpgradeProps extends UpgradeInfoProps {
  show: boolean;
}

interface Props extends Pick<InputProps, 'disabled'> {
  onConfirm: ((value: number) => void) | ((value: string) => void);
  value?: string | number;
  displayName?: string | number;
  inputProps?: OptionInputProps;
  label?: string | React.ReactElement;
  loading?: boolean;
  placeholder?: string;
  inputType?: InputProps['type'];
  helperText?: InputProps['description'];
  upgrade?: UpgradeProps;
  viewOnly?: boolean;
}

interface InputFormProps extends Omit<Props, 'placeholder' | 'loading' | 'upgrade' | 'viewOnly'> {
  onClose: () => void;
}

const InputForm: FC<InputFormProps> = ({
  inputType: type = 'text',
  inputProps,
  value,
  label,
  disabled,
  helperText: description,
  onConfirm: onSubmitConfirm,
  onClose,
  ...restProps
}) => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { isDirty, errors },
  } = useForm<Form>({
    defaultValues: {
      inputField: value,
    },
  });

  const onSubmit = ({ inputField }: Form) => {
    if (isDirty) {
      (onSubmitConfirm as (value: number | string) => void)(inputField);
    }
    onClose();
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
    }
  };

  const inputPropsWithDefaults = useMemo(() => {
    const pattern = inputProps?.pattern;
    const withBaseDefaults = {
      ...inputProps,
      step: type === 'number' ? (inputProps?.step ?? 1) : undefined,
      pattern: typeof pattern === 'string' ? new RegExp(pattern) : undefined,
    };
    if (typeof pattern === 'object') {
      return {
        ...withBaseDefaults,
        pattern: {
          value: pattern.value === undefined ? undefined : new RegExp(pattern.value),
          message: pattern.message,
        },
      };
    }
    return withBaseDefaults;
  }, [type, inputProps]);

  const errorMessage = useMemo(() => {
    if (!errors?.inputField) {
      return undefined;
    }

    const inputField = errors.inputField as FieldError;
    if (inputField.message) {
      return inputField.message;
    }
    return t(`inputErrors.basic.${inputField.type}`, {
      value: (inputPropsWithDefaults as Record<string, string | number | RegExp>)[inputField.type],
      name: label,
    });
  }, [t, label, errors.inputField, inputPropsWithDefaults]);

  const { step: stepWithDefault } = inputPropsWithDefaults;
  const stepValue = typeof stepWithDefault === 'object' ? stepWithDefault.value : stepWithDefault;

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <StyledListItem>
        <ListContent type="container">
          <Input
            {...register('inputField', {
              valueAsNumber: type === 'number',
              validate: {
                step: (targetValue: string | number | undefined) => {
                  if (type === 'number' && stepValue) {
                    const inputMessage =
                      typeof stepWithDefault === 'object' ? stepWithDefault.message : undefined;
                    const isStepValid =
                      floatSafeRemainder(Number(targetValue || 0), stepValue) === 0;
                    return isStepValid || (inputMessage ?? false);
                  }
                  return true;
                },
              },
              ...inputPropsWithDefaults,
            } as RegisterOptions<Form, 'inputField'>)}
            step={stepValue}
            type={type}
            onKeyDown={handleKeyDown}
            description={description}
            disabled={disabled}
            error={errorMessage}
            aria-label="input"
            autoFocus
            {...restProps}
          />
        </ListContent>

        <Box display="flex" mb={description ? 24 : 0} flexDirection="row-reverse">
          <Box ml={12}>
            <ListContent type="icon">
              <Button
                type="submit"
                icon={IconCheckmark}
                disabled={!isDirty || (isDirty && !!errors.inputField)}
                aria-label="submit"
              />
            </ListContent>
          </Box>
          <Box>
            <ListContent type="icon">
              <Button type="button" icon={IconCloseSmall} onClick={onClose} aria-label="close" />
            </ListContent>
          </Box>
        </Box>
      </StyledListItem>
    </form>
  );
};

const EditTextField: FC<Props> = ({
  placeholder,
  loading,
  upgrade: { show: showUpgradeInfo, ...upgradeInfoProps } = {} as UpgradeProps,
  viewOnly,
  ...formProps
}) => {
  const [editing, setEditing] = useState<boolean>(false);

  const onEdit = () => setEditing(true);
  const onClose = () => setEditing(false);

  return editing ? (
    <InputForm {...formProps} onClose={onClose} />
  ) : (
    <ListItem>
      <ListContent type="text" secondaryTop={formProps.label}>
        {formProps.inputType === 'secret'
          ? '********'
          : (formProps.displayName ?? formProps.value ?? <Box color="grey30">{placeholder}</Box>)}
      </ListContent>
      {!viewOnly && (
        <ListContent type="icon">
          {showUpgradeInfo ? (
            <UpgradeInfo {...upgradeInfoProps} />
          ) : (
            <Button
              variant="ghost"
              onClick={onEdit}
              disabled={formProps.disabled}
              icon={IconEdit}
              loading={loading}
              aria-label="edit"
            />
          )}
        </ListContent>
      )}
    </ListItem>
  );
};

export default EditTextField;
